// src/pages/AdminUserApproval.js

import { useEffect, useMemo, useState, useRef } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { useAuth } from "../services/authService";
import { sendApprovalEmail } from "../services/EmailService";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassHalf,
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";
import "./AdminUserApproval.scss";

export default function AdminUserApproval() {
  const { currentUser, userRole } = useAuth();
  const topRef = useRef(null);

  const PAGE_SIZE = 5;

  const [allVisibleUsers, setAllVisibleUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [totalVisibleUsers, setTotalVisibleUsers] = useState(null);

  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);

  const visibleRequestedRole = useMemo(() => {
    if (userRole === "client_admin") return "client";
    if (userRole === "service_admin") return "service";
    return null;
  }, [userRole]);

  const normalizeRequestedRole = (data) => {
    if (data?.requestedRole === "service") return "service";
    if (data?.requestedRole === "client") return "client";

    if (
      data?.adminRole === "service_admin" ||
      data?.adminRole === "firmware_admin"
    ) {
      return "service";
    }

    if (data?.adminRole === "client_admin") {
      return "client";
    }

    if (
      data?.role === "service" ||
      data?.role === "service_user" ||
      data?.role === "service_admin"
    ) {
      return "service";
    }

    return "client";
  };

  const isAdminAccount = (dataOrUser) => {
    const role = String(dataOrUser?.role || "").trim();
    const adminRole = String(dataOrUser?.adminRole || "").trim();

    return (
      [
        "admin",
        "super_admin",
        "superadmin",
        "service_admin",
        "client_admin",
        "firmware_admin",
      ].includes(adminRole) ||
      ["admin", "super_admin", "service_admin", "client_admin"].includes(role)
    );
  };

  const mapUserDoc = (docSnap) => {
    const data = docSnap.data() || {};
    const fullName = `${data.name || ""} ${data.surname || ""}`.trim();

    let createdAtDate = "—";
    let createdAtTime = "";
    let createdAtValue = 0;

    if (data.createdAt && typeof data.createdAt.toDate === "function") {
      const dateObj = data.createdAt.toDate();
      createdAtDate = dateObj.toLocaleDateString("cs-CZ");
      createdAtTime = dateObj.toLocaleTimeString("cs-CZ");
      createdAtValue = dateObj.getTime();
    }

    const requestedRole = normalizeRequestedRole(data);

    const status =
      data.status === "rejected" ? "denied" : data.status || "pending";

    return {
      id: docSnap.id,
      name: fullName || "—",
      email: data.email || "—",
      phone: data.phone || "—",
      company: data.company || "—",
      contactPerson: data.contactPerson || "—",
      status,
      requestedRole,
      role: data.role || null,
      adminRole: data.adminRole || null,
      isAdmin: isAdminAccount(data),
      createdAtDate,
      createdAtTime,
      createdAtValue,
      doc: docSnap,
    };
  };

  const shouldIncludeUser = (user) => {
    if (visibleRequestedRole === "client") {
      return user.requestedRole === "client";
    }

    if (visibleRequestedRole === "service") {
      return user.requestedRole === "service";
    }

    return true;
  };

  const loadVisibleUsers = async () => {
    setLoading(true);

    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }

    try {
      const usersRef = collection(db, "users");
      const snap = await getDocs(usersRef);

      const visible = snap.docs
        .map(mapUserDoc)
        .filter(shouldIncludeUser)
        .sort((a, b) => b.createdAtValue - a.createdAtValue);

      setAllVisibleUsers(visible);
      setTotalVisibleUsers(visible.length);

      setPage(1);
      setUsers(visible.slice(0, PAGE_SIZE));
      setHasNext(visible.length > PAGE_SIZE);
    } catch (error) {
      console.error("Chyba při načítání uživatelů:", error);
      setAllVisibleUsers([]);
      setUsers([]);
      setHasNext(false);
      setTotalVisibleUsers(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVisibleUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleRequestedRole]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return users;

    return users.filter((u) => {
      const hay =
        `${u.name} ${u.email} ${u.company} ${u.phone} ${u.contactPerson}`.toLowerCase();

      return hay.includes(q);
    });
  }, [users, search]);

  const updateVisiblePage = (targetPage) => {
    const start = (targetPage - 1) * PAGE_SIZE;
    const nextPageItems = allVisibleUsers.slice(start, start + PAGE_SIZE);

    setUsers(nextPageItems);
    setPage(targetPage);
    setHasNext(start + PAGE_SIZE < allVisibleUsers.length);

    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const updatedUser = allVisibleUsers.find((u) => u.id === id);

      if (updatedUser?.isAdmin) {
        alert("Admin účet nelze z tohoto seznamu schvalovat ani zamítat.");
        return;
      }

      const updates = { status: newStatus };

      if (newStatus === "approved" && updatedUser) {
        updates.role = updatedUser.requestedRole || "client";
      }

      await updateDoc(doc(db, "users", id), updates);

      if (newStatus === "approved" && updatedUser?.email) {
        await sendApprovalEmail({
          to_email: updatedUser.email,
          to_name: updatedUser.name,
        });
      }

      await loadVisibleUsers();
    } catch (error) {
      console.error("Nepodařilo se aktualizovat stav uživatele:", error);
    }
  };

  const deleteUser = async (id) => {
    if (currentUser?.uid === id) {
      alert("Nemůžete smazat sám sebe.");
      return;
    }

    const targetUser = allVisibleUsers.find((u) => u.id === id);

    if (targetUser?.isAdmin) {
      alert("Admin účet nelze z tohoto seznamu smazat.");
      return;
    }

    const confirmDelete = window.confirm(
      "Opravdu chcete uživatele trvale smazat?",
    );

    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "users", id));
      await loadVisibleUsers();
    } catch (error) {
      alert(
        "Nepodařilo se smazat uživatele. Zkontrolujte oprávnění ve Firestore rules.",
      );

      console.error(error);
    }
  };

  const getRoleLabel = (requestedRole, isAdmin) => {
    if (isAdmin && requestedRole === "service") return "Servisní admin";
    if (isAdmin && requestedRole === "client") return "Klientský admin";
    if (requestedRole === "service") return "Servisní technik";
    return "Klient";
  };

  const renderActions = (user) => {
    if (user.isAdmin) {
      return <span className="admin-protected">Admin účet</span>;
    }

    return (
      <div className="action-buttons">
        {(user.status === "pending" || user.status === "denied") && (
          <button
            className="approve pulse-approve"
            onClick={() => updateStatus(user.id, "approved")}
          >
            Schválit
          </button>
        )}

        {(user.status === "pending" || user.status === "approved") && (
          <button
            className="deny"
            onClick={() => updateStatus(user.id, "denied")}
          >
            Zamítnout
          </button>
        )}

        <button className="delete" onClick={() => deleteUser(user.id)}>
          Smazat
        </button>
      </div>
    );
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case "approved":
        return (
          <>
            <span className="status-icon approved" title="Schválen">
              <FaCheckCircle />
            </span>
            <span className="status-text">Schválen</span>
          </>
        );

      case "pending":
        return (
          <>
            <span className="status-icon pending" title="Čeká na schválení">
              <FaHourglassHalf />
            </span>
            <span className="status-text">Čeká</span>
          </>
        );

      case "denied":
        return (
          <>
            <span className="status-icon rejected" title="Zamítnut">
              <FaTimesCircle />
            </span>
            <span className="status-text">Zamítnut</span>
          </>
        );

      default:
        return <span>—</span>;
    }
  };

  const modeLabel = useMemo(() => {
    if (userRole === "client_admin") return "Režim: client_admin";

    if (userRole === "service_admin") {
      return "Režim: schvalování servisních techniků";
    }

    if (userRole === "super_admin") {
      return "Režim: superadmin (vidí vše)";
    }

    return "";
  }, [userRole]);

  const goPrev = () => {
    if (page <= 1) return;
    updateVisiblePage(page - 1);
  };

  const goNext = () => {
    if (!hasNext) return;
    updateVisiblePage(page + 1);
  };

  return (
    <div className="admin-approval-page" ref={topRef}>
      <h1>
        Seznam registrovaných uživatelů
        {typeof totalVisibleUsers === "number" && (
          <span className="heading-count">({totalVisibleUsers})</span>
        )}
      </h1>

      {modeLabel && <p className="mode-line">{modeLabel}</p>}

      <div className="admin-search">
        <label htmlFor="adminSearch">Hledat:</label>

        <input
          id="adminSearch"
          type="text"
          placeholder="jméno / email / firma / tel"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <p>Načítání uživatelů...</p>
      ) : filteredUsers.length === 0 ? (
        <p>Žádní uživatelé nenalezeni.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Jméno:</th>
              <th>Email:</th>
              <th>Telefon:</th>
              <th>Společnost:</th>
              <th>Ověřovací kontakt:</th>
              <th>Typ účtu:</th>
              <th>Stav:</th>
              <th>Registrován:</th>
              <th>Akce:</th>
            </tr>
          </thead>

          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id}>
                <td>
                  <span className="label">Jméno:</span>
                  <span className="value">{user.name}</span>
                </td>

                <td>
                  <span className="label">Email:</span>
                  <span className="value">{user.email}</span>
                </td>

                <td>
                  <span className="label">Telefon:</span>
                  <span className="value">{user.phone}</span>
                </td>

                <td>
                  <span className="label">Společnost:</span>
                  <span className="value">{user.company}</span>
                </td>

                <td>
                  <span className="label">Ověřovací kontakt:</span>
                  <span className="value">{user.contactPerson}</span>
                </td>

                <td>
                  <span className="label">Typ účtu:</span>
                  <span className="value">
                    {getRoleLabel(user.requestedRole, user.isAdmin)}
                  </span>
                </td>

                <td>
                  <span className="label">Stav:</span>

                  <span className="value status-cell">
                    {getStatusDisplay(user.status)}
                  </span>
                </td>

                <td>
                  <span className="label">Registrován:</span>

                  <span className="value registered">
                    <span className="date">{user.createdAtDate}</span>{" "}
                    <span className="time">{user.createdAtTime}</span>
                  </span>
                </td>

                <td>
                  <span className="label">Akce:</span>
                  <span className="value">{renderActions(user)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(users.length > 0 || hasNext || page > 1) && !loading && (
        <div className="pagination-wrapper">
          <button
            className="pagination-btn"
            onClick={goPrev}
            disabled={page <= 1 || loading}
          >
            <FaArrowLeft className="icon" />
            <span className="text">Předchozí</span>
          </button>

          <div className="page-info">
            Stránka <b>{page}</b>
          </div>

          <button
            className="pagination-btn"
            onClick={goNext}
            disabled={!hasNext || loading}
          >
            <FaArrowRight className="icon" />
            <span className="text">Další</span>
          </button>
        </div>
      )}
    </div>
  );
}
